/**
 * @fileoverview Dialog for choosing the primary case when linking to a standalone matter.
 *
 * Shown when user tries to link to a matter that's not already in a companion group.
 * Lets them choose which case should be the primary of the new group.
 */
import { Crown } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface PrimaryPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentMatterName: string;
  targetMatterName: string;
  onConfirm: (primaryMatterId: 'current' | 'target') => void;
  isLoading: boolean;
}

export const PrimaryPickerDialog: React.FC<PrimaryPickerDialogProps> = ({
  open,
  onOpenChange,
  currentMatterName,
  targetMatterName,
  onConfirm,
  isLoading,
}) => {
  const [selectedPrimary, setSelectedPrimary] = useState<'current' | 'target'>('target');

  const handleConfirm = () => {
    onConfirm(selectedPrimary);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5 text-sm">
            <Crown className="h-4 w-4 text-amber-500" />
            Create Companion Group
          </DialogTitle>
          <DialogDescription className="text-xs">
            Neither case is in a companion group. Which should be primary?
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={selectedPrimary}
          onValueChange={(v) => setSelectedPrimary(v as 'current' | 'target')}
          className="space-y-2 py-2"
        >
          <div className="flex items-start gap-2">
            <RadioGroupItem value="target" id="primary-target" className="mt-0.5" />
            <div className="flex-1">
              <Label
                htmlFor="primary-target"
                className="text-xs font-medium text-zinc-900 cursor-pointer"
              >
                {targetMatterName}
              </Label>
              <p className="text-xs text-zinc-500">The case you selected</p>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <RadioGroupItem value="current" id="primary-current" className="mt-0.5" />
            <div className="flex-1">
              <Label
                htmlFor="primary-current"
                className="text-xs font-medium text-zinc-900 cursor-pointer"
              >
                {currentMatterName}
              </Label>
              <p className="text-xs text-zinc-500">This case</p>
            </div>
          </div>
        </RadioGroup>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
